/** E-mail + password sign-in and "Forgot password" (no self sign-up, plan §7.3). */
import { type FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { friendlyAuthError, useAuth } from "../auth/AuthContext";
import { Field, Notice } from "../components/ui";

export function LoginPage() {
  const { user, ready, signIn, sendReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signin" | "reset">("signin");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  if (ready && user) return <Navigate to={from} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signin") {
        await signIn(email, password);
        navigate(from, { replace: true });
      } else {
        await sendReset(email);
        setSent(true);
      }
    } catch (reason) {
      // Enumeration protection: a reset request always reports success.
      if (mode === "reset") setSent(true);
      else setError(friendlyAuthError(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <form className="auth-card stack" onSubmit={submit} aria-labelledby="login-title">
        <img style={{ flex: 1 }} src="/nsgs-logo.png" alt="NSGS" />
        <div>
          <h1 id="login-title" style={{ fontSize: "1.45rem" }}>
            {mode === "signin" ? "Sign in to SAT Analyzer" : "Set or reset your password"}
          </h1>
          <p className="muted small">
            {mode === "signin"
              ? "BESS commissioning test and annual SAT assurance."
              : "We will e-mail you a link to set a new password."}
          </p>
        </div>
        {error && <Notice kind="error">{error}</Notice>}
        {sent && (
          <Notice kind="success">
            If an account exists for {email}, an e-mail with a password link is on its way. The link is valid for a short
            time.
          </Notice>
        )}
        <Field label="E-mail address">
          <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {mode === "signin" && (
          <Field label="Password">
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}
        <button className="btn primary block" type="submit" disabled={busy}>
          {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Send password link"}
        </button>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => {
            setMode(mode === "signin" ? "reset" : "signin");
            setError(null);
            setSent(false);
          }}
        >
          {mode === "signin" ? "Forgot password?" : "Back to sign in"}
        </button>
        <p className="muted small">Accounts are created by invitation only.</p>
      </form>
    </div>
  );
}
